// PHASE_0 B0 deliverable 2 — application-composite page-header primitive.
//
// The standard top-of-content block every screen renders inside the shell's
// content slot: an <h1> title, optional description, and an actions slot
// (e.g. "Create employee"). Content is caller-supplied so the host owns i18n
// keys; the component owns the heading semantics + layout. The title renders
// as a real <h1> so each routed Content contributes one document heading
// (WCAG 2.4.6 / 1.3.1).

import { forwardRef, type HTMLAttributes, type ReactNode } from "react";

import { cx } from "../lib/cx.ts";

export interface PageHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  /** Page title — rendered as the content's <h1>. */
  readonly title: ReactNode;
  /** Optional supporting copy under the title. */
  readonly description?: ReactNode;
  /** Optional trailing actions (buttons, menus). */
  readonly actions?: ReactNode;
}

export const PageHeader = forwardRef<HTMLDivElement, PageHeaderProps>(
  ({ title, description, actions, className, ...rest }, ref) => (
    <div
      ref={ref}
      data-component="page-header"
      className={cx("nai-page-header", className)}
      {...rest}
    >
      <div data-slot="page-header-text">
        <h1 className="nai-page-header-title">{title}</h1>
        {description !== undefined && (
          <p className="nai-page-header-description">{description}</p>
        )}
      </div>
      {actions !== undefined && (
        <div data-slot="page-header-actions">{actions}</div>
      )}
    </div>
  ),
);
PageHeader.displayName = "PageHeader";
